import {createReducer} from "redux-act";
import {
	addPlayerToTeam,
	autofillSuccess,
	autoFillTeam,
	autoFillTeamFailed,
	autoFillTeamSuccess,
	clearTeam,
	clearTeamsByID,
	clearWithdrawn,
	hidePlayerModal,
	hideSaveTeamSuccessModal,
	IRequestedTeamPayload,
	ITeam, removeBooster, removeBoosterFailed, removeBoosterSuccess,
	removePlayerFromTeam,
	requestMyTeam,
	requestMyTeamFailed,
	requestMyTeamSuccess,
	requestRivalTeamSuccess,
	saveBooster, saveBoosterFailed, saveBoosterSuccess,
	saveTeam,
	saveTeamFailed,
	saveTeamSuccess,
	setCaptain,
	showPlayerModal,
	updateTeamLineup,
} from "modules/actions";
import {RequestState} from "modules/enums";
import {cloneDeep, get} from "lodash";
import {defaultPlayers} from "modules/constants";

interface IReducer {
	byEvent: Record<number, ITeam>;
	requestState: RequestState;
	boosterRequestState: RequestState;
	hasUnsavedChanges: boolean;
	showDetailsForPlayerID: null | number;
	showSuccessSaveModal: boolean;
	lastPlayerIDCard: string;
}

const defaultState: IReducer = {
	byEvent: {},
	requestState: RequestState.IDLE,
	boosterRequestState: RequestState.IDLE,
	hasUnsavedChanges: false,
	showDetailsForPlayerID: null,
	showSuccessSaveModal: false,
	lastPlayerIDCard: "",
};

/**
 * The list of players will be stored in the players field,
 * the lineup and bench fields contain data from BE and are used only for previous seasons
 */
const defaultTeam = {
	id: 0,
	name: "",
	/**
	 * For previous seasons
	 */
	lineup: [],
	/**
	 * For previous seasons
	 */
	bench: [],
	/**
	 * Actual team lineup
	 */
	players: cloneDeep(defaultPlayers),
	captain: null,
	start_event_id: 0,
	points: null,
	scoreflow: {},
	selections: {},
	createdAt: "",
};

const createPlayersList = (lineup: number[] | null, bench: number[] | null) => {
	const lineupWithBench = [...(lineup || []), ...(bench || [])];

	return defaultPlayers.map(({position}, index) => ({
		position,
		playerID: lineupWithBench[index] || null
	}))
}

const onSuccessTeamSave = (state: IReducer) => ({
	...state,
	requestState: RequestState.Received,
	showSuccessSaveModal: true,
});

const onFailedTeamSave = (state: IReducer) => ({
	...state,
	requestState: RequestState.Received,
});

const onSuccessReceiveTeam = (state: IReducer, {team, eventID}: IRequestedTeamPayload) => ({
	...state,
	hasUnsavedChanges: false,
	requestState: RequestState.Received,
	byEvent: {
		...state.byEvent,
		[eventID]: {
			...defaultTeam,
			...team,
			players: createPlayersList(team.lineup, team.bench),
			isTeamCreated: Boolean(team.lineup.length),
		},
	},
});

const onAutofillSuccess = (state: IReducer, {team, eventID}: IRequestedTeamPayload) => ({
	...state,
	hasUnsavedChanges: true,
	requestState: RequestState.Received,
	byEvent: {
		...state.byEvent,
		[eventID]: {
			...defaultTeam,
			...team,
			players: createPlayersList(team.lineup, team.bench),
			isTeamCreated: Boolean(team.lineup.length),
		},
	},
});

export const team = createReducer<typeof defaultState>({}, defaultState)
	.on(saveTeam, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(autoFillTeam, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(saveTeamSuccess, onSuccessTeamSave)
	.on(saveTeamFailed, onFailedTeamSave)
	.on(autoFillTeamSuccess, onSuccessTeamSave)
	.on(autoFillTeamFailed, onFailedTeamSave)
	.on(hideSaveTeamSuccessModal, (state) => ({
		...state,
		requestState: RequestState.Received,
		showSuccessSaveModal: false,
	}))
	.on(requestMyTeam, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(requestMyTeamSuccess, onSuccessReceiveTeam)
	.on(autofillSuccess, onAutofillSuccess)
	.on(requestMyTeamFailed, (state) => ({
		...state,
		requestState: RequestState.IDLE,
	}))
	.on(requestRivalTeamSuccess, onSuccessReceiveTeam)
	.on(clearTeam, (state, eventID) => ({
		...state,
		hasUnsavedChanges: true,
		requestState: RequestState.IDLE,
		byEvent: {
			...state.byEvent,
			[eventID]: {
				...state.byEvent?.[eventID],
				captain: null,
				lineup: [],
				bench: [],
				players: cloneDeep(defaultPlayers),
			},
		},
	}))
	.on(clearWithdrawn, (state, eventID) => ({
		...state,
		byEvent: {
			...state.byEvent,
			[eventID]: {
				...state.byEvent?.[eventID],
				withdrawn: null,
			},
		},
	}))
	.on(addPlayerToTeam, (state, {eventID, playerID}) => {
		const team = state.byEvent?.[eventID];
		const teamPlayers = get(team, "players", cloneDeep(defaultPlayers))
		const firstNullIndex = teamPlayers.findIndex((player) => player.playerID === null);
		const players = teamPlayers.map((player, index) => {
			if (firstNullIndex === index) {
				return {
					...player,
					playerID
				}
			}

			return player
		});

		return {
			...state,
			hasUnsavedChanges: true,
			requestState: RequestState.IDLE,
			lastPlayerIDCard: `player-card-id-${playerID}`,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					players,
				},
			},
		};
	})
	.on(removePlayerFromTeam, (state, {eventID, playerID}) => {
		const team = state.byEvent?.[eventID];
		const captain = team?.captain;

		return {
			...state,
			hasUnsavedChanges: true,
			requestState: RequestState.IDLE,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					players: team?.players.map((player) => player.playerID === playerID ? {...player, playerID: null} : player),
					captain: captain === playerID ? null : captain,
				},
			},
		};
	})
	.on(updateTeamLineup, (state, {players, eventID}) => {
		const team = state.byEvent?.[eventID];

		return {
			...state,
			hasUnsavedChanges: true,
			requestState: RequestState.IDLE,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					players,
				},
			},
		};
	})
	.on(setCaptain, (state, {eventID, playerID}) => {
		const team = state.byEvent?.[eventID];

		return {
			...state,
			hasUnsavedChanges: true,
			requestState: RequestState.IDLE,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					captain: playerID,
				},
			},
		};
	})
	.on(showPlayerModal, (state, playerID) => ({
		...state,
		showDetailsForPlayerID: playerID,
	}))
	.on(hidePlayerModal, (state) => ({
		...state,
		showDetailsForPlayerID: null,
	}))
	.on(clearTeamsByID, (state) => ({
		...state,
		byEvent: defaultState.byEvent,
	}))
	.on(saveBooster, (state) => ({
		...state,
		boosterRequestState: RequestState.Requested,
	}))
	.on(saveBoosterSuccess, (state, {booster, eventID}) => {
		const team = state.byEvent?.[eventID];

		return {
			...state,
			boosterRequestState: RequestState.Received,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					boosters: [...team.boosters, booster],
				},
			},
		};
	})
	.on(saveBoosterFailed, (state) => ({
		...state,
		boosterRequestState: RequestState.Received,
	}))
	.on(removeBooster, (state) => ({
		...state,
		boosterRequestState: RequestState.Requested,
	}))
	.on(removeBoosterSuccess, (state, {boosterId, eventID}) => {
		const team = state.byEvent?.[eventID];

		return {
			...state,
			boosterRequestState: RequestState.Received,
			byEvent: {
				...state.byEvent,
				[eventID]: {
					...team,
					boosters: team.boosters.filter(({id}) => id !== boosterId),
				},
			},
		};
	})
	.on(removeBoosterFailed, (state) => ({
		...state,
		boosterRequestState: RequestState.Received,
	}))

